import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Audio, AVPlaybackStatus } from "expo-av";
import { usePostHog } from "posthog-js/react";

interface AudioPlayerContextType {
  isPlaying: boolean;
  position: number;
  duration: number;
  soundId: string | null;
  loadingId: string | null;
  sliderValue: number;
  isSeeking: boolean;
  wasPlayingBeforeSeek: boolean;
  isTrackbarVisible: boolean;
  volume: number;
  playbackRate: number;
  handlePlayPause: (audioUrl?: string, callId?: string) => Promise<void>;
  startSeeking: () => void;
  seekUpdate: (value: string) => void;
  finishSeeking: () => Promise<void>;
  handleRestart: () => Promise<void>;
  handleVolume: (newVolume: number) => Promise<void>;
  handlePlaybackRate: (newRate: number) => Promise<void>;
  handleCloseTrackbar: () => Promise<void>;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(
  undefined
);

export function AudioPlayerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const posthog = usePostHog();

  const [sound, setSound] = useState<Audio.Sound | null>(null);
  const [soundId, setSoundId] = useState<string | null>(null);
  const soundRef = useRef<Audio.Sound | null>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [wasPlayingBeforeSeek, setWasPlayingBeforeSeek] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const lastPositionRef = useRef(-1);
  const [isTrackbarVisible, setIsTrackbarVisible] = useState(false);

  const [volume, setVolume] = useState(100);
  const [playbackRate, setPlaybackRate] = useState(1);

  const [loadingId, setLoadingId] = useState<string | null>(null);

  const reset = async () => {
    await soundRef.current?.stopAsync();
    await soundRef.current?.unloadAsync();
    soundRef.current = null;
    setSound(null);
    setSoundId(null);
    setPosition(0);
    setSliderValue(0);
    setIsPlaying(false);
    lastPositionRef.current = -1;
    setIsTrackbarVisible(false);
    setIsSeeking(false);
    setWasPlayingBeforeSeek(false);
  };

  const onPlaybackStatusUpdate = (status: AVPlaybackStatus) => {
    if (status.isLoaded) {
      setIsTrackbarVisible(true);
      if (!isSeeking) {
        const millis = status.positionMillis;
        setSliderValue(Math.max(millis, lastPositionRef.current));
        lastPositionRef.current = Math.max(millis, lastPositionRef.current);
      }
      setPosition(status.positionMillis);
      setDuration(status.durationMillis || 0);
      setIsPlaying(status.isPlaying);

      if (status.didJustFinish) {
        setIsPlaying(false);
        lastPositionRef.current = -1;
      }
    }
  };

  const handlePlayPause = useCallback(
    async (audioUrl?: string, callId?: string) => {
      const id = callId || soundId;
      try {
        setLoadingId(id);

        if (sound && id !== soundId) {
          await reset();
        }

        if (id === soundId && sound) {
          const status = await sound.getStatusAsync();
          if (status.isLoaded) {
            if (status.isPlaying) {
              posthog.capture("audio_paused", {
                call_id: id,
                position: status.positionMillis,
                duration: status.durationMillis,
              });
              await sound.pauseAsync();
              setIsPlaying(false);
            } else {
              posthog.capture("audio_played", {
                call_id: id,
                position: status.positionMillis,
                duration: status.durationMillis,
              });
              await sound.playAsync();
              setIsPlaying(true);
            }
          }
          setLoadingId(null);
          return;
        }

        const { sound: newSound } = await Audio.Sound.createAsync(
          { uri: audioUrl || "" },
          { shouldPlay: true },
          onPlaybackStatusUpdate
        );

        setSliderValue(0);
        lastPositionRef.current = -1;
        await newSound.setRateAsync(playbackRate, true);
        await newSound.setVolumeAsync(volume / 100);
        await newSound.playAsync();
        setSound(newSound);
        setSoundId(id);
        soundRef.current = newSound;
        setIsPlaying(true);
        setPosition(0);
      } catch (error) {
        console.error("Error handling audio:", error);
      } finally {
        setLoadingId(null);
      }
    },
    [sound, soundId, posthog, playbackRate, volume]
  );

  const startSeeking = useCallback(() => {
    setIsSeeking(true);
    lastPositionRef.current = -1;
    setWasPlayingBeforeSeek(isPlaying);
    if (sound && isPlaying) {
      sound.pauseAsync();
    }
  }, [sound, isPlaying]);

  const seekUpdate = useCallback(
    (value: string) => {
      const newValue = parseInt(value);
      if (isSeeking) {
        setSliderValue(newValue);
      }
    },
    [isSeeking]
  );

  const finishSeeking = useCallback(async () => {
    if (sound) {
      posthog.capture("audio_seek_complete", {
        call_id: soundId,
        position: sliderValue,
        duration: duration,
        was_playing: wasPlayingBeforeSeek,
      });
      await sound.setPositionAsync(sliderValue);
      lastPositionRef.current = sliderValue;
      setPosition(sliderValue);
      setSliderValue(sliderValue);
      setIsSeeking(false);
      if (wasPlayingBeforeSeek) {
        await sound.playAsync();
      }
    }
  }, [sound, soundId, sliderValue, duration, wasPlayingBeforeSeek, posthog]);

  const handleRestart = useCallback(async () => {
    if (sound) {
      posthog.capture("audio_restarted", {
        call_id: soundId,
        previous_position: position,
      });
      await sound.setPositionAsync(0);
      lastPositionRef.current = 0;
      setPosition(0);
      setSliderValue(0);
    }
  }, [sound, soundId, position, posthog]);

  const handleVolume = useCallback(
    async (newVolume: number) => {
      if (sound) {
        posthog.capture("audio_volume_changed", {
          call_id: soundId,
          volume: newVolume,
        });
        setVolume(newVolume);
        await sound.setVolumeAsync(newVolume / 100);
      }
    },
    [sound, soundId, posthog]
  );

  const handlePlaybackRate = useCallback(
    async (newRate: number) => {
      if (sound) {
        posthog.capture("audio_playback_rate_changed", {
          call_id: soundId,
          rate: newRate,
        });
        await sound.setRateAsync(newRate, true);
        setPlaybackRate(newRate);
      }
    },
    [sound, soundId, posthog]
  );

  const handleCloseTrackbar = useCallback(async () => {
    posthog.capture("audio_trackbar_closed", {
      call_id: soundId,
      position: position,
      duration: duration,
    });
    await reset();
  }, [soundId, position, duration, posthog]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        isPlaying,
        position,
        duration,
        soundId,
        loadingId,
        sliderValue,
        isSeeking,
        wasPlayingBeforeSeek,
        isTrackbarVisible,
        volume,
        playbackRate,
        handlePlayPause,
        startSeeking,
        seekUpdate,
        finishSeeking,
        handleRestart,
        handleVolume,
        handlePlaybackRate,
        handleCloseTrackbar,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (context === undefined) {
    throw new Error(
      "useAudioPlayer must be used within an AudioPlayerProvider"
    );
  }
  return context;
}
